import React from 'react';
import { Link } from 'react-router-dom';
import { WalletTransaction } from '@/types/wallet';
import { formatCurrencyAuto, formatDate } from '@/utils/formatters';
import TransactionTypeIcon from './TransactionTypeIcon';
import TransactionStatusBadge from './TransactionStatusBadge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface TransactionListProps {
  transactions: WalletTransaction[];
  loading?: boolean;
  emptyMessage?: string;
  onTransactionClick?: (transaction: WalletTransaction) => void;
  showViewAll?: boolean;
  viewAllLink?: string;
}

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  loading = false,
  emptyMessage = 'No transactions yet',
  onTransactionClick,
  showViewAll = false,
  viewAllLink = '/dashboard/wallet/transactions',
}) => {
  const getTransactionLabel = (type: string): string => {
    const labels: Record<string, string> = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer_in: 'Transfer Received',
      transfer_out: 'Transfer Sent',
      escrow_release: 'Escrow Released',
      escrow_hold: 'Escrow Hold',
      escrow_refund: 'Escrow Refund',
      commission_deduction: 'Commission',
      refund: 'Refund',
      bonus: 'Bonus',
      penalty: 'Penalty',
      adjustment: 'Adjustment',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-500 dark:text-neutral-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {transactions.map((transaction) => (
        <div
          key={transaction._id}
          onClick={() => onTransactionClick && onTransactionClick(transaction)}
          className={`rounded-xl border border-slate-200 bg-slate-50/40 p-3 dark:border-gray-800 dark:bg-gray-900/40 sm:p-4 ${
            onTransactionClick ? 'cursor-pointer transition-colors hover:border-primary-400' : ''
          }`}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className="mt-0.5">
                <TransactionTypeIcon type={transaction.type} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    {getTransactionLabel(transaction.type)}
                  </h4>
                  <TransactionStatusBadge status={transaction.status} size="sm" />
                </div>
                <p className="line-clamp-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                  {transaction.description}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-gray-500 sm:text-xs">
                  <span>{formatDate(String(transaction.createdAt))}</span>
                  <span className="max-w-[170px] truncate">ID: {transaction.transactionId}</span>
                </div>
              </div>
            </div>

            <div className="self-start sm:ml-4 sm:text-right">
              <p
                className={`text-base font-semibold sm:text-lg ${
                  transaction.direction === 'credit'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {transaction.direction === 'credit' ? '+' : '-'}
                {formatCurrencyAuto(transaction.amount, transaction.currency, undefined, true)}
              </p>
              {transaction.fee > 0 && (
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-gray-500 sm:mt-1 sm:text-xs">
                  Fee: {formatCurrencyAuto(transaction.fee, transaction.currency, undefined, true)}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {showViewAll && (
        <div className="text-center pt-2">
          <Link
            to={viewAllLink}
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            View All Transactions →
          </Link>
        </div>
      )}
    </div>
  );
};

export default TransactionList;
